// Channel mapping as GROUPS.
//
// Two earlier shapes were wrong. A flat "unmapped values" list stacked both vocabularies
// together and hid which value corresponded to which. Two side-by-side tables showed the
// relationship twice — once under "paired with", once under "feeds" — and could not express
// one platform reporting two Statex channels.
//
// A group is one bucket: the Statex channels and the actual-spend values that describe the
// same spend. Many-to-many falls out of that in both directions, and it states the real
// constraint rather than hiding it — if Meta Ads reports Facebook and Instagram as one
// figure, those two cannot be calibrated apart, and putting them in one group is what
// saying so looks like.

import { useState, useEffect, useCallback } from 'react';
import { cmApi } from '../../api.js';
import { useClient } from '../../ClientContext.jsx';
import { T } from '../../../../shared/styles/tokens.js';

const SOURCE_LABEL = { paid_media: 'Paid Media', offline: 'Offline', direct_buys: 'Direct Buys' };

const aKey = (a) => `${a.channel}|${a.publisher || ''}`;
const aName = (a) => (a.publisher ? `${a.channel} › ${a.publisher}` : a.channel);
const EMPTY = { channel_key: '', publisher_key: '', statex: new Set(), actuals: new Set(), replaces: null };

export default function ChannelGroupsSection() {
  const { clientId } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);   // null = not editing

  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    cmApi.getChannelGroups(clientId)
      .then(setData)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(load, [load]);

  const startNew = () => setDraft({ ...EMPTY, statex: new Set(), actuals: new Set() });

  const startEdit = (g) => setDraft({
    channel_key: g.channel_key,
    publisher_key: g.publisher_key || '',
    statex: new Set(g.statex.map(s => s.value)),
    actuals: new Set(g.actuals.map(aKey)),
    replaces: { channel_key: g.channel_key, publisher_key: g.publisher_key },
  });

  const toggle = (field, value) => setDraft(d => {
    const next = new Set(d[field]);
    next.has(value) ? next.delete(value) : next.add(value);
    return { ...d, [field]: next };
  });

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const picked = (data.all_actuals || []).filter(a => draft.actuals.has(aKey(a)));
      await cmApi.saveChannelGroup(clientId, {
        channel_key: draft.channel_key.trim(),
        publisher_key: draft.publisher_key.trim() || draft.channel_key.trim(),
        statex: [...draft.statex],
        actuals: picked.map(a => ({ channel: a.channel, publisher: a.publisher })),
        replaces: draft.replaces,
      });
      setDraft(null);
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const remove = async (g) => {
    setBusy(true); setErr(null);
    try {
      await cmApi.deleteChannelGroup(clientId, {
        channel_key: g.channel_key, publisher_key: g.publisher_key,
      });
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!clientId) return <p className="o-state">Select a client to map channels.</p>;
  if (loading) return <p className="o-state">Loading…</p>;

  const groups = data?.groups || [];
  const allStatex = data?.all_statex || [];
  const allActuals = data?.all_actuals || [];
  const looseStatex = data?.unassigned_statex || [];
  const looseActuals = data?.unassigned_actuals || [];

  // A value already in another group; selecting it here moves it, which is worth showing.
  const ownerOf = (field, value) => {
    for (const g of groups) {
      if (draft?.replaces
          && g.channel_key === draft.replaces.channel_key
          && g.publisher_key === draft.replaces.publisher_key) continue;
      const hit = field === 'statex'
        ? g.statex.some(s => s.value === value)
        : g.actuals.some(a => aKey(a) === value);
      if (hit) return g;
    }
    return null;
  };

  const chip = (field, value, label, meta) => {
    const on = draft[field].has(value);
    const owner = !on && ownerOf(field, value);
    // Source moves to the tooltip: it reads the same on nearly every chip, so inline it
    // was noise rather than information.
    const hint = [meta, owner && `currently in ${owner.publisher_key} — selecting moves it here`]
      .filter(Boolean).join(' · ');
    return (
      <button key={value} type="button" className="o-chip o-wide" aria-pressed={on}
        onClick={() => toggle(field, value)}
        title={hint || undefined}
        style={{ opacity: owner ? 0.55 : 1 }}>
        {label}
      </button>
    );
  };

  return (
    <div>
      <p className="o-note">
        A group is one bucket of spend described by both sides — the Statex channels and the
        actual-spend values that report the same thing. Put as many of each in a group as
        belong together. Where one platform reports two Statex channels as a single figure,
        grouping them is what makes that explicit: they are calibrated together because the
        data cannot separate them.
      </p>

      {err && <div className="o-state o-error" style={{ textAlign: 'left', padding: `${T.s2}px 0` }}>{err}</div>}

      {(looseStatex.length > 0 || looseActuals.length > 0) && !draft && (
        <p className="o-hint">
          <b>{looseStatex.length} Statex channel{looseStatex.length === 1 ? '' : 's'} and{' '}
          {looseActuals.length} actual-spend value{looseActuals.length === 1 ? '' : 's'} are
          in no group.</b> They only match an identical spelling on the other side, so most
          will not contribute to a factor until grouped.
        </p>
      )}

      <div className="o-head-row" style={{ marginBottom: T.s4 }}>
        <p className="o-eyebrow">Groups · {groups.length}</p>
        {!draft && (
          <button className="o-btn o-btn-primary" onClick={startNew}>New group</button>
        )}
      </div>

      {/* ── Editor ── */}
      {draft && (
        <div className="o-panel" style={{ padding: `${T.s4}px ${T.s5}px`, marginBottom: T.s5 }}>
          <div className="o-ctrl-row" style={{ padding: 0, border: 0, marginBottom: T.s4 }}>
            <div className="o-ctrl">
              <label className="o-field-lbl">Channel</label>
              <input className="o-control" placeholder="Social" value={draft.channel_key}
                onChange={e => setDraft(d => ({ ...d, channel_key: e.target.value }))} />
            </div>
            <div className="o-ctrl">
              <label className="o-field-lbl">Publisher</label>
              <input className="o-control" placeholder="Meta" value={draft.publisher_key}
                onChange={e => setDraft(d => ({ ...d, publisher_key: e.target.value }))} />
            </div>
            <div className="o-ctrl" style={{ justifyContent: 'flex-end' }}>
              <div style={{ display: 'flex', gap: T.s2 }}>
                <button className="o-btn o-btn-primary" onClick={save}
                  disabled={busy || !draft.channel_key.trim()
                            || (!draft.statex.size && !draft.actuals.size)}>
                  {busy ? 'Saving…' : 'Save group'}
                </button>
                <button className="o-btn" onClick={() => setDraft(null)}>Cancel</button>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: T.s5 }}>
            <div>
              <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
                Statex competitive · {draft.statex.size} selected
              </p>
              <div className="o-chipset">
                {allStatex.map(s => chip('statex', s.value, s.value,
                                         Number(s.rows).toLocaleString()))}
              </div>
            </div>
            <div>
              <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
                Actual spend · {draft.actuals.size} selected
              </p>
              <div className="o-chipset">
                {allActuals.map(a => chip('actuals', aKey(a), aName(a),
                                          SOURCE_LABEL[a.source] || a.source))}
              </div>
            </div>
          </div>

          <p className="o-note" style={{ margin: `${T.s3}px 0 0` }}>
            Dimmed values already belong to another group; selecting one moves it here.
          </p>
        </div>
      )}

      {/* ── Groups: one row, both sides ── */}
      <div className="o-panel" style={{ overflowX: 'auto' }}>
        <table className="o-tbl">
          <thead>
            <tr>
              <th>Group</th>
              <th>Statex competitive</th>
              <th>Actual spend</th>
              <th>Rows</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: T.text3 }}>
                No groups yet. Nothing will match unless both sides happen to spell a value
                identically.
              </td></tr>
            )}
            {groups.map(g => (
              <tr key={`${g.channel_key}|${g.publisher_key}`}>
                <td className="o-strong">
                  {g.channel_key}
                  {g.publisher_key && g.publisher_key !== g.channel_key && (
                    <span style={{ color: T.text3 }}> › {g.publisher_key}</span>
                  )}
                  {!g.client_specific && (
                    <span className="o-badge o-grey" style={{ marginLeft: 6 }}>default</span>
                  )}
                </td>
                <td>
                  {g.statex.length === 0
                    ? <span style={{ color: T.text3 }}>none — no competitive side</span>
                    : g.statex.map(s => (
                        <span key={s.value} className="o-badge o-grey" style={{ marginRight: 4 }}>
                          {s.value}
                        </span>
                      ))}
                </td>
                <td>
                  {g.actuals.length === 0
                    ? <span style={{ color: T.text3 }}>none — no actuals to calibrate from</span>
                    : g.actuals.map(a => (
                        <span key={aKey(a)} className="o-badge o-grey" style={{ marginRight: 4 }}>
                          {aName(a)}
                        </span>
                      ))}
                </td>
                <td className="o-num" style={{ whiteSpace: 'nowrap' }}>
                  {Number(g.statex_rows).toLocaleString()} / {Number(g.actual_rows).toLocaleString()}
                </td>
                <td className="o-acts">
                  <button className="o-btn o-btn-sm" disabled={busy}
                    onClick={() => startEdit(g)}>Edit</button>
                  {g.client_specific && (
                    <button className="o-btn o-btn-sm" disabled={busy}
                      onClick={() => remove(g)}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── What is still loose ── */}
      {(looseStatex.length > 0 || looseActuals.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: T.s5, marginTop: T.s5 }}>
          <div>
            <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
              Ungrouped Statex · {looseStatex.length}
            </p>
            <div className="o-chipset">
              {looseStatex.length === 0
                ? <span style={{ fontSize: T.tSmall, color: T.text3 }}>none</span>
                : looseStatex.map(s => (
                    <span key={s.value} className="o-badge o-grey">
                      {s.value} · {Number(s.rows).toLocaleString()}
                    </span>
                  ))}
            </div>
          </div>
          <div>
            <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
              Ungrouped actual spend · {looseActuals.length}
            </p>
            <div className="o-chipset">
              {looseActuals.length === 0
                ? <span style={{ fontSize: T.tSmall, color: T.text3 }}>none</span>
                : looseActuals.map(a => (
                    <span key={aKey(a)} className="o-badge o-grey">
                      {aName(a)} · {Number(a.rows).toLocaleString()}
                    </span>
                  ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
